<template>
  <dx-modal
    id="update-cbhpm-modal"
    title="Atualização via CBHPM"
    size="lg"
    :value="true"
    @input="close"
  >
    <div class="loading loading-lg" v-if="loading" />
    <template v-else>
      <div class="h6 text-secondary">Serviços, taxas e outras despesas</div>
      <table class="table table-hover">
        <thead>
        <tr>
          <th class="text-center">
            <fa-icon class="text-info" :icon="['fal', 'info-circle']" />
          </th>
          <th>Código</th>
          <th>Nome</th>
          <th class="text-right">Valor atual</th>
          <th class="text-right">Novo valor</th>
        </tr>
        </thead>
        <tbody>
        <tr v-for="(item, i) in filteredExpenses" :key="i">
          <td class="text-center">
            <span class="tooltip tooltip-right" data-tooltip="Será atualizado"
                  v-if="item.definition.updateValuesByCbhpm
                   && item.definition.newInsuranceValue">
              <fa-icon class="text-success" :icon="['fal', 'check-circle']" />
            </span>
            <span class="tooltip tooltip-right"
                  data-tooltip="Bloqueado para atualizações"
                  v-else-if="!item.definition.updateValuesByCbhpm">
              <fa-icon class="text-warning" :icon="['fal', 'lock']" />
            </span>
            <span class="tooltip tooltip-right"
                  data-tooltip="Preço não encontrado na tabela"
                  v-else>
              <fa-icon class="text-error" :icon="['fal', 'times-circle']" />
            </span>
          </td>
          <td>{{ item.code | tuss }}</td>
          <td>{{ item.name }}</td>
          <td class="text-right">{{ item.definition.insuranceValue | currency }}</td>
          <td class="text-right">
            <strong class="text-secondary"
                    v-if="item.definition.updateValuesByCbhpm
                    && item.definition.newInsuranceValue">
              {{ item.definition.newInsuranceValue | currency }}
            </strong>
            <strong class="text-center tooltip tooltip-left"
                    data-tooltip="Bloqueado para atualizações"
                    v-else-if="!item.definition.updateValuesByCbhpm">
              <fa-icon class="text-warning" :icon="['fal', 'lock']" />
            </strong>
            <strong class="text-error text-center tooltip tooltip-left"
                    data-tooltip="Preço não encontrado na tabela"
                    v-else>N/A</strong>
          </td>
        </tr>
        </tbody>
      </table>
    </template>
    <template v-slot:footer>
      <button class="btn btn-primary btn-icon btn-icon-left mr-1"
              :class="{loading: saving}"
              :disabled="saving"
              @click="save">
        <fa-icon :icon="['fal', 'check']" />Atualizar
      </button>
      <button class="btn" @click="close">Sair</button>
    </template>
  </dx-modal>
</template>

<script>
import { clearNonNumbers } from '@/helpers/string';
import { mergeFrom } from '@/helpers/object';

export default {
  props: {
    tableId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      saving: false,
      form: this.blankForm(),
      CBHPMExpenses: [],
    };
  },
  mounted() {
    this.init();
  },
  computed: {
    filteredExpenses() {
      return this.form.expenses.filter(item => item.code
        && item.definition.updateValuesByCbhpm
        && !['medicine', 'material'].includes(item.expense.type))
        .map(item => ({
          id: item.expense.id,
          code: item.code,
          name: item.expense.name,
          type: item.expense.type,
          definition: item.definition,
        }));
    },
  },
  methods: {
    async init() {
      this.loading = true;
      try {
        await this.loadTable();
        await this.loadCBHPM();
        this.setNewInsuranceValue();
      } catch (e) {
        this.$toast.error('Ocorreu um erro ao carregar os valores da CBHPM!');
      } finally {
        this.loading = false;
      }
    },
    loadTable() {
      return this.$http.get(`/expense-tables/${this.tableId}`)
        .then(({ data }) => {
          this.form = mergeFrom(this.blankForm(), data);
        });
    },
    loadCBHPM() {
      const edition = this.form.cbhpm && this.form.cbhpm.edition
        ? this.form.cbhpm.edition.code
        : null;

      if (!edition) {
        this.$toast.error('Tabela sem edição CBHPM definida. Salve a tabela antes de atualizar.');
        this.CBHPMExpenses = [];
        return Promise.resolve();
      }

      const params = {
        edition,
        codes: this.filteredExpenses.map(({ code }) => clearNonNumbers(code)).join(','),
      };

      return this.$http.get('/cbhpm-expenses', { params })
        .then(({ data }) => {
          this.CBHPMExpenses = data.items;
        });
    },
    setNewInsuranceValue() {
      this.filteredExpenses.forEach((item) => {
        const foundCBHPM = this.CBHPMExpenses
          .find(({ code }) => code === clearNonNumbers(item.code));

        const foundPostage = foundCBHPM && foundCBHPM.postage
          ? (this.form.cbhpm.postageItems || [])
            .find(({ postage }) => postage === foundCBHPM.postage.postage)
          : null;

        if (foundPostage) {
          const procedureFilm = this.form.cbhpm.film || 0;
          const procedureOperationalCost = this.form.cbhpm.operationalCost || 0;
          const cbhpmFilmQtd = foundCBHPM.film || 0;
          const cbhpmOperationalCost = foundCBHPM.operationalCost || 0;
          const cbhpmFraction = foundCBHPM.postage.fraction || 1;
          const feeValue = (foundPostage.value * cbhpmFraction) || 0;
          const operationalCost = (procedureOperationalCost * cbhpmOperationalCost);
          item.definition.filmQuantity = cbhpmFilmQtd;
          item.definition.filmValue = procedureFilm;
          item.definition.operationalCostValue = operationalCost;
          item.definition.feeValue = feeValue;
          item.definition.newInsuranceValue = (
            operationalCost + (procedureFilm * cbhpmFilmQtd) + feeValue
          );
        }
      });
    },
    close() {
      this.$emit('close');
    },
    save() {
      if (this.saving) {
        return;
      }

      this.$dialog.show('', {
        html:
          '<div class="text-center">'
          + '<h5 class="text-center">Atenção!</h5>'
          + '<div>Deseja realmente atualizar os valores da tabela?</div>'
          + '</div>',
        buttons: [
          {
            label: 'Não',
            classes: '',
          }, {
            label: 'Sim',
            classes: 'btn-primary btn-error ml-2',
            click: (close) => {
              close();
              this.saving = true;
              const payload = {
                expenses: this.filteredExpenses
                  .filter(({ definition }) => definition.newInsuranceValue)
                  .map(item => ({
                    id: item.id,
                    definition: {
                      insuranceValue: item.definition.newInsuranceValue,
                      filmQuantity: item.definition.filmQuantity,
                      filmValue: item.definition.filmValue,
                      operationalCostValue: item.definition.operationalCostValue,
                      feeValue: item.definition.feeValue,
                    },
                  })),
              };

              return this.$http.put(`/expense-tables/${this.tableId}/cbhpm`, payload)
                .then(() => {
                  this.$emit('saved');
                  this.$toast.success('Registro(s) atualizado(s) com sucesso!');
                })
                .catch(() => {
                  this.$toast.error('Ocorreu um erro ao salvar!');
                })
                .finally(() => {
                  this.saving = false;
                });
            },
          },
        ],
      });
    },
    blankForm() {
      return {
        id: null,
        type: '',
        name: '',
        startDate: '',
        endDate: '',
        cbhpm: '',
        notes: '',
        expenses: [],
      };
    },
  },
};
</script>
